/**
 * The claim's payee picker, in one place.
 *
 * Both routes that record a payment - the workflow's Record Payment action and
 * the ledger's Add Payment - offer the same picker over the same parties, so
 * the provider and the prefill rule live here rather than in each dialog.
 */

import { useMemo } from 'react'

import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'

/** One party this claim could pay, read off the claim the caller already holds. */
export interface PayeeCandidate {
  /** Empty for a free-text payee the operator typed. */
  uuid: string
  name: string
  /** One of the tenant's ``recipient_type`` options. */
  recipient_type: string
  secondary?: string
}

/** The provider key an ENTITY_SEARCH field names to get this picker. */
export const CLAIM_PAYEE_PROVIDER = 'claim_payee'

/**
 * Shared lookup providers plus ``claim_payee``, which searches the parties on
 * this claim rather than a global list - so it needs no endpoint.
 */
export function useClaimPayeeProviders(candidates: PayeeCandidate[]) {
  const base = useResourceLookupProviders()
  return useMemo(
    () => ({
      ...base,
      [CLAIM_PAYEE_PROVIDER]: {
        search: async (term: string) => {
          const needle = term.trim().toLowerCase()
          return candidates
            .filter((c) => !needle || c.name.toLowerCase().includes(needle))
            .map((c) => ({
              uuid: c.uuid,
              name: c.name,
              secondary: c.secondary,
            }))
        },
      },
    }),
    [base, candidates],
  )
}

/**
 * What the picker starts as. One candidate prefills; several stay blank so the
 * choice is made deliberately rather than accepted by reflex.
 */
export function defaultPayeeValue(
  candidates: PayeeCandidate[],
): { uuid: string; name: string } | '' {
  return candidates.length === 1
    ? { uuid: candidates[0].uuid, name: candidates[0].name }
    : ''
}
